import RouteGate from '@components/RouteGate'
import { AuthLayout } from '@layouts/AuthLayout'
import LayoutLogged from '@modules/auth/layouts/LayoutLogged'
import { LayoutNotLogged } from '@modules/auth/layouts/LayoutNotLogged'
import ForgotPasswordPage from '@pages/auth/ForgotPasswordPage'
import GoingToLogoutPage from '@pages/auth/GoingToLogoutPage'
import LoginPage from '@pages/auth/LoginPage'
import RegisterPage from '@pages/auth/RegisterPage'
import ResetPasswordPage from '@pages/auth/ResetPasswordPage'
import TwoFactorAuthenticationPage from '@pages/auth/TwoFactorAuthenticationPage'
import ForbiddenPage from '@pages/ForbiddenPage'
import NotFoundPage from '@pages/NotFoundPage'
import VenueBillingPage from '@pages/venue-billing/VenueBillingPage'
import { useLayoutEffect } from 'react'
import { Navigate, RouteObject, useLocation, useRoutes } from 'react-router-dom'
import App from '../App'
import businessRoutes from './app/business/BusinessRoutes'
import helpRoutes from './app/help/HelpRoutes'
import marketingRoutes from './app/marketing/MarketingRoutes'
import membershipRoutes from './app/membership/MembershipRoutes'
import overviewRoutes from './app/overview/OverviewRoutes'
import settingsRoutes from './app/settings/SettingsRoutes'
import userRoutes from './app/users/UserRoutes'
import venueRoutes from './app/venues/VenueRoutes'

// Define the routes as an array of RouteObject
const appRoutes: RouteObject[] = [
  {
    element: <App />,
    children: [
      {
        element: <LayoutLogged />,
        children: [
          {
            // Authorization for every logged route, resolved from
            // ROUTE_PERMISSIONS. Routes declare nothing themselves.
            element: <RouteGate />,
            children: [
              { path: '/overview', children: overviewRoutes },
              { path: '/users', children: userRoutes },
              { path: '/venues', children: venueRoutes },
              { path: '/marketing', children: marketingRoutes },
              { path: '/membership', children: membershipRoutes },
              { path: '/business', children: businessRoutes },
              { path: '/settings', children: settingsRoutes },
              { path: '/help', children: helpRoutes },
              // Internal billing tracker — intentionally not in the menu, reachable
              // by URL only and gated by the accessVenueBilling permission.
              { path: '/venue-billing', element: <VenueBillingPage /> },
              { path: '/403', element: <ForbiddenPage /> },
              { path: '/404', element: <NotFoundPage /> },
              { index: true, element: <Navigate to="/overview" replace /> },
              { path: '*', element: <Navigate to="/404" replace /> },
            ],
          },
        ],
      },

      {
        path: '/auth',
        element: <AuthLayout />,
        children: [
          {
            element: <LayoutNotLogged />,
            children: [
              { path: 'login', element: <LoginPage /> },
              { path: 'register', element: <RegisterPage /> },
              { path: 'forgot-password', element: <ForgotPasswordPage /> },
              { path: 'reset-password', element: <ResetPasswordPage /> },
            ],
          },
          { path: '2FA', element: <TwoFactorAuthenticationPage /> },
          { path: 'going-to-logout', element: <GoingToLogoutPage /> },
          {
            path: '*',
            index: true,
            element: <Navigate to="/auth/login" replace />,
          },
        ],
      },
    ],
  },
]

const AppRoutes: React.FC = () => {
  const routing = useRoutes(appRoutes)
  const location = useLocation()

  useLayoutEffect(() => {
    // Scroll to top on route change
    window.scrollTo(0, 0)
  }, [location.pathname])

  return <>{routing}</>
}

export default AppRoutes
